import { debounce } from '../../utils/lodash-replacements';
import ScrollCarousel, { measureSlidesPerView } from '../../components/ScrollCarousel';

const SELECTORS = {
  viewport: '[data-cms-product-slider-viewport]',
  track: '[data-cms-product-slider-track]',
};

const sliders = [];

const destroySliders = () => {
  sliders.forEach((slider) => {
    slider.destroy();
  });
  // The array was never emptied before, so each later teardown re-destroyed
  // instances that were already gone.
  sliders.length = 0;
};

const handleSlider = () => {
  const carousels = [];

  document.querySelectorAll('[data-cms-product-slider]').forEach((slider) => {
    const tiles = slider.querySelectorAll('[data-product-tile]');
    if (tiles.length === 0) {
      return;
    }

    const tileWidth = tiles[0].offsetWidth;
    // Images can still be loading immediately after the module is injected.
    // A zero width is not evidence that the slider fits; wait for a load or
    // resize pass before making the overflow decision.
    if (tileWidth === 0) {
      return;
    }

    const containerWidth = slider.offsetWidth;

    // Nothing overflows the container, so there is nothing to slide to.
    if (tiles.length * tileWidth < containerWidth) {
      return;
    }

    const viewport = slider.querySelector(SELECTORS.viewport);
    if (!viewport) {
      return;
    }

    carousels.push({ slider, tiles, viewport });
  });

  if (sliders.length > 0) {
    destroySliders();
  }

  carousels.forEach(({ slider, tiles, viewport }) => {
    const carousel = new ScrollCarousel(slider, {
      selectors: SELECTORS,
      // Flickity `wrapAround: true`. `contain` is a CSS concern now
      // (scroll-snap-align on the tiles).
      loop: true,
      prevNextButtons: true,
      pageDots: true,
      draggable: true,
      // Several tiles are on screen at once, so the loop must clone a full
      // viewport's worth or the seam shows a gap.
      slidesPerView: measureSlidesPerView(viewport, tiles[0]),
      ariaLabel: 'Product slider',
    });
    sliders.push(carousel);
  });
};

const handleSliderAfterLayout = debounce(handleSlider, 250);
const observedImages = new WeakSet();

const bindImageLoadHandlers = () => {
  document.querySelectorAll('[data-cms-product-slider] img').forEach((image) => {
    if (observedImages.has(image)) {
      return;
    }

    observedImages.add(image);
    image.addEventListener('load', handleSliderAfterLayout);
    image.addEventListener('error', handleSliderAfterLayout);
  });
};

const initSlider = () => {
  window.addEventListener('resize', handleSliderAfterLayout);
  bindImageLoadHandlers();
  // Attempt the first measurement immediately; the image handlers above
  // retry after unloaded images establish their final tile dimensions.
  handleSlider();
};

export { destroySliders, initSlider };
